import { Fragment, useEffect, useState } from "react";
import { Star, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/src/components/ui/dialog";
import { useFetch } from "@/src/lib/useFetch";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";

/**
 * Chartered-before flag + pooled history module (Phase 1, B2).
 * Flags are fetched in ONE batched call per results page (no per-card
 * requests); the history table loads lazily when the badge is opened.
 * History pools HELM + Anchor bookings with the client's trip-survey rating
 * where one was filed. Broker-internal: full client names are shown here.
 */

export type CharterFlag = { yachtId: string; count: number; avgRating: number | null; yachtCount?: number };

export type TripSurveyDetail = {
  charterType: string;
  scores: { label: string; value: number }[];
  answers: { label: string; answer: string }[];
  notes: { question: string; answer: string }[];
};

export type CharterHistoryEntry = {
  bookingSlug: string;
  clientName: string | null;
  yachtName?: string | null;
  isThisYacht?: boolean;
  brand: string | null;
  charterType: string | null;
  startDate: string;
  endDate: string;
  rating: number | null;
  comment?: string | null;
  survey?: TripSurveyDetail | null;
};

export function useCharterFlags(yachtIds: string[]) {
  const [flags, setFlags] = useState<Record<string, CharterFlag>>({});
  const { fetch } = useFetch();

  useEffect(() => {
    if (!yachtIds.length) {
      setFlags({});
      return;
    }
    let cancelled = false;
    fetch<{ data: CharterFlag[] }>("/api/yachts/charter-flags", "POST", { yachtIds })
      .then((r) => {
        if (cancelled) return;
        const map: Record<string, CharterFlag> = {};
        for (const f of r.data ?? []) map[f.yachtId] = f;
        setFlags(map);
      })
      .catch(() => {
        /* flags are an enhancement — never block the results list */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yachtIds.join(",")]);

  return flags;
}

export function CharterFlagBadge({ flag, yachtName }: { flag?: CharterFlag; yachtName: string }) {
  const [open, setOpen] = useState(false);
  if (!flag || !flag.count) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-0.5 text-xs font-medium hover:bg-emerald-100"
        title="Chartered before — view history"
      >
        {flag.yachtCount ? `Chartered ${flag.yachtCount}×` : `Operator chartered ${flag.count}×`}
        {flag.avgRating != null && (
          <span className="inline-flex items-center gap-0.5">
            · <Star size={12} className="fill-current" /> {flag.avgRating.toFixed(1)}
          </span>
        )}
      </button>
      {open && <CharterHistoryDialog yachtId={flag.yachtId} yachtName={yachtName} open={open} setOpen={setOpen} />}
    </>
  );
}

function SurveyDetailPanel({ survey }: { survey: TripSurveyDetail }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 p-3 flex flex-col gap-3">
      {survey.scores.length > 0 && (
        <div className="flex flex-wrap gap-x-6 gap-y-1.5">
          {survey.scores.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1.5 text-xs">
              <span className="text-slate-500">{s.label}</span>
              <span className="inline-flex items-center gap-0.5 text-amber-500">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={11} className={n <= Math.round(s.value) ? "fill-current" : "text-slate-200"} />
                ))}
              </span>
              <b className="text-slate-700">{s.value.toFixed(1)}</b>
            </span>
          ))}
        </div>
      )}
      {survey.answers.length > 0 && (
        <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-xs">
          {survey.answers.map((a, i) => (
            <div key={i} className="contents">
              <span className="text-slate-500">{a.label}</span>
              <span className="text-slate-800">{a.answer}</span>
            </div>
          ))}
        </div>
      )}
      {survey.notes.map((n, i) => (
        <div key={i} className="text-xs">
          <div className="text-slate-500">{n.question}</div>
          <p className="text-slate-800 leading-snug mt-0.5">“{n.answer}”</p>
        </div>
      ))}
      {survey.scores.length === 0 && survey.answers.length === 0 && survey.notes.length === 0 && (
        <span className="text-xs text-slate-400">Survey filed, but no rateable answers recorded.</span>
      )}
    </div>
  );
}

export function CharterHistoryDialog(props: { yachtId: string; yachtName: string; open: boolean; setOpen: (v: boolean) => void }) {
  const [entries, setEntries] = useState<CharterHistoryEntry[] | null>(null);
  const [openRow, setOpenRow] = useState<number | null>(null);
  const { fetch } = useFetch();

  useEffect(() => {
    fetch<{ data: CharterHistoryEntry[] }>(`/api/yachts/${props.yachtId}/charter-history`)
      .then((r) => setEntries(r.data ?? []))
      .catch(() => setEntries([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.yachtId]);

  return (
    <Dialog open={props.open} onOpenChange={props.setOpen}>
      <DialogContent className="sm:max-w-[680px] max-h-[calc(100%-80px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Charter history — {props.yachtName}</DialogTitle>
        </DialogHeader>
        {entries === null ? (
          <p className="text-sm text-slate-500 py-4">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">No completed charters recorded for this yacht.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2 pr-3 font-medium">Dates</th>
                <th className="py-2 pr-3 font-medium">Yacht</th>
                <th className="py-2 pr-3 font-medium">Client</th>
                <th className="py-2 pr-3 font-medium">Brand</th>
                <th className="py-2 pr-3 font-medium">Type</th>
                <th className="py-2 font-medium">Trip rating</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => {
                const hasSurvey = e.rating != null && !!e.survey;
                const isOpen = openRow === i;
                return (
                  <Fragment key={i}>
                    <tr className={isOpen ? "" : "border-b last:border-b-0"}>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {formatDateUTC(e.startDate, "UTC")} – {formatDateUTC(e.endDate, "UTC")}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={e.isThisYacht ? "font-semibold" : ""}>
                          {e.yachtName ?? "—"}
                          {e.isThisYacht && <span className="text-xs text-blue-600 ml-1">(this yacht)</span>}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        {e.clientName ?? <span className="text-slate-400">—</span>}
                        {e.bookingSlug && <span className="text-xs text-slate-400 ml-1.5">({e.bookingSlug})</span>}
                      </td>
                      <td className="py-2 pr-3 capitalize">{e.brand ?? "—"}</td>
                      <td className="py-2 pr-3 capitalize">{e.charterType ?? "—"}</td>
                      <td className="py-2">
                        {e.rating != null ? (
                          hasSurvey ? (
                            <button
                              type="button"
                              onClick={() => setOpenRow(isOpen ? null : i)}
                              className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                              title="View the completed trip survey"
                            >
                              <Star size={13} className="fill-amber-400 text-amber-400" /> {e.rating.toFixed(1)}
                              <ChevronDown size={13} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <Star size={13} className="fill-amber-400 text-amber-400" /> {e.rating.toFixed(1)}
                            </span>
                          )
                        ) : (
                          <span className="text-slate-400">no survey</span>
                        )}
                      </td>
                    </tr>
                    {isOpen && e.survey && (
                      <tr className="border-b last:border-b-0">
                        <td colSpan={6} className="pb-3 pt-0 px-0">
                          <SurveyDetailPanel survey={e.survey} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="text-xs text-slate-400 mt-2">
          Pooled across HELM and Anchor. Ratings come from client trip surveys; broker-internal view.
        </p>
      </DialogContent>
    </Dialog>
  );
}


export type YachtCommission = { yacht_id: string; commission: number; commission_type: "percentage" | "fixed" };

/** Batched operator commissions for the search cards (prototype ycard__comm). */
export function useYachtCommissions(yachtIds: string[]) {
  const [map, setMap] = useState<Record<string, YachtCommission>>({});
  const { fetch } = useFetch();
  const key = yachtIds.slice().sort().join(",");
  useEffect(() => {
    if (!yachtIds.length) return;
    let cancelled = false;
    fetch<{ data: YachtCommission[] }>("/api/yachts/commissions", "POST", { yachtIds })
      .then((r) => {
        if (cancelled) return;
        const next: Record<string, YachtCommission> = {};
        for (const row of r.data ?? []) next[row.yacht_id] = row;
        setMap(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return map;
}

/**
 * On-card reviews block (prototype ycard__rev): stars + average + count with
 * an expandable mini-review list drawn from the operator-pooled history.
 * Renders nothing when there is no history at all.
 */
export function CardReviews({ flag, yachtId }: { flag?: CharterFlag; yachtId: string }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<CharterHistoryEntry[] | null>(null);
  const { fetch } = useFetch();

  if (!flag || !flag.count) return null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && entries === null) {
      try {
        const r = await fetch<{ data: CharterHistoryEntry[] }>(`/api/yachts/${yachtId}/charter-history`);
        setEntries(r.data ?? []);
      } catch {
        setEntries([]);
      }
    }
  };

  return (
    <div className="border-t border-blue-50 mt-3 pt-2.5">
      <div className="flex items-center gap-2 text-sm">
        {flag.avgRating != null && (
          <span className="flex items-center gap-0.5 text-amber-500">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                size={13}
                className={n <= Math.round(flag.avgRating!) ? "fill-current" : "text-slate-200"}
              />
            ))}
            <b className="text-slate-900 ml-1">{flag.avgRating.toFixed(1)}</b>
          </span>
        )}
        <span className="text-slate-500">
          · {flag.count} trip review{flag.count === 1 ? "" : "s"}
          {flag.yachtCount ? ` (${flag.yachtCount} this yacht · ${flag.count - flag.yachtCount} fleet)` : " (operator fleet)"}
        </span>
        <button type="button" onClick={toggle} className="ml-auto text-blue-600 text-sm hover:underline">
          {open ? "Hide" : "Read"}
        </button>
      </div>
      {open && (
        <div className="flex flex-col gap-2.5 mt-2.5">
          {entries === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : (
            <>
              {entries.slice(0, 6).map((e, i) => (
                <div key={i} className="rounded-md bg-slate-50 p-2.5">
                  <div className="flex items-center gap-2 text-xs">
                    {e.rating != null && (
                      <span className="flex items-center gap-0.5 text-amber-500">
                        <Star size={11} className="fill-current" /> {e.rating.toFixed(1)}
                      </span>
                    )}
                    <span className="font-medium">{e.clientName ?? "Client"}</span>
                    <span
                      className={
                        "rounded px-1.5 py-0.5 " +
                        (e.isThisYacht ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600")
                      }
                    >
                      {e.isThisYacht ? "this yacht" : (e.yachtName ?? "fleet")}
                    </span>
                    <span className="text-slate-400 ml-auto capitalize">{e.brand ?? ""}</span>
                  </div>
                  {e.comment && <p className="text-sm mt-1.5 leading-snug">“{e.comment}”</p>}
                </div>
              ))}
              <p className="text-xs text-slate-400">
                Trip reviews from every past charter where the guest filed feedback — this yacht first, then the
                operator's wider fleet.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
